import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroBanner from '../components/HeroBanner';
import ProductCard from '../components/ProductCard';
import SEO, { organizationSchema, websiteSchema } from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { useSettings } from '../context/SettingsContext';
import api from '../api/client';
import { Truck, ShoppingBag, RotateCcw, Wrench, ArrowRight, Loader2, Sparkles, Package, Tag, Heart, Home, Hammer, Star } from 'lucide-react';
import { resolveImage } from '../components/admin/ImageUpload';

const ICONS = { Truck, ShoppingBag, RotateCcw, Wrench, Sparkles, Package, Tag, Heart, Home, Hammer, Star };

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deals, setDeals] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const promoTiles = settings.promo_tiles || [];

  useEffect(() => {
    Promise.all([
      api.get('/products'),
      api.get('/categories'),
      api.get('/products/homepage/deals').catch(() => ({ data: [] })),
      api.get('/products/homepage/trending').catch(() => ({ data: [] })),
    ]).then(([p, c, d, t]) => {
      setProducts(p.data);
      setCategories(c.data);
      setDeals(d.data || []);
      setTrending(t.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <SEO
        title="Trade-price tools, materials & site essentials"
        description="Dr Tools — UK trade specialists. Cordless drills, safety kit, plumbing, electrical, decorating and building supplies with bulk trade pricing, next-day delivery and open credit accounts for builders and retailers."
        path="/"
        jsonLd={[organizationSchema(), websiteSchema()]}
      />
      <h1 className="sr-only">Dr Tools — trade-price tools, materials & site essentials</h1>
      <HeroBanner />

      <section className="max-w-[1400px] mx-auto px-4 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {promoTiles.map((t, i) => {
            const Icon = ICONS[t.icon] || Sparkles;
            return (
              <div key={i} className="bg-neutral-50 border border-neutral-200 p-4 rounded-sm flex items-start gap-3">
                <div className="bg-[#F46A25] text-white p-2 rounded-sm"><Icon className="w-5 h-5" /></div>
                <div>
                  <div className="font-bold text-sm text-[#12263A]">{t.title}</div>
                  <div className="text-xs text-neutral-600">{t.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 py-8">
        <div className="flex items-end justify-between mb-5">
          <h2 className="text-2xl md:text-3xl font-black text-[#12263A]">Shop by category</h2>
          <Link to="/category/tools" className="text-sm font-semibold text-[#F46A25] hover:underline flex items-center gap-1">See all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25]" /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {categories.filter(c => !c.parent_id).map(c => (
              <Link key={c.id} to={`/category/${c.slug}`} className="group bg-white border border-neutral-200 hover:border-[#F46A25] rounded-sm overflow-hidden transition-all hover:shadow-md">
                <div className="aspect-square overflow-hidden bg-neutral-50">
                  <img src={resolveImage(c.image)} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3 text-center text-sm font-semibold text-[#12263A] group-hover:text-[#F46A25]">{c.name}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {deals.length > 0 && (
        <section className="bg-[#FFF6EF] py-10 mt-4" data-testid="homepage-deals">
          <div className="max-w-[1400px] mx-auto px-4">
            <div className="flex items-end justify-between mb-5">
              <div>
                <span className="inline-block bg-[#F46A25] text-white text-xs font-bold px-3 py-1 rounded-sm mb-2">Save now</span>
                <h2 className="text-2xl md:text-3xl font-black text-[#12263A]">Deals of the week</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
              {deals.map(p => <ProductCard key={`${p.id}-${p.variant_id || 'base'}`} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {trending.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 py-10" data-testid="homepage-trending">
          <div className="flex items-end justify-between mb-5">
            <h2 className="text-2xl md:text-3xl font-black text-[#12263A]">Trending products</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            {trending.map(p => <ProductCard key={`${p.id}-${p.variant_id || 'base'}`} product={p} />)}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-4 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative h-64 rounded-sm overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=1200" alt="Kitchen ideas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-2xl font-bold mb-1">Kitchen inspiration</h3>
              <p className="text-sm mb-3">Design ideas for every space</p>
              <Link to="/category/kitchens" className="inline-block bg-[#F46A25] hover:bg-white hover:text-[#12263A] text-white font-bold text-sm px-4 py-2 rounded-sm transition-colors">Get inspired</Link>
            </div>
          </div>
          <div className="relative h-64 rounded-sm overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1200" alt="Garden ideas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-2xl font-bold mb-1">Garden ready</h3>
              <p className="text-sm mb-3">Everything for outdoor living</p>
              <Link to="/category/garden" className="inline-block bg-[#F46A25] hover:bg-white hover:text-[#12263A] text-white font-bold text-sm px-4 py-2 rounded-sm transition-colors">Shop garden</Link>
            </div>
          </div>
        </div>
      </section>

      <FAQSection slug="home" heading="Frequently asked questions" subheading="Everything trade customers ask before switching to Dr Tools." />
    </div>
  );
};

export default HomePage;
